import React from 'react';
import type { FieldPath, FieldValues, UseFormClearErrors, UseFormSetError, UseFormTrigger } from 'react-hook-form';
import { useShallow } from 'zustand/shallow';

import { usePaidBreakExceptionStore } from '#/shared/stores/paidBreakExceptionStore';

import { setExceptionsToError, validatePaidBreakExceptions } from '../utils/paidBreakExceptionHelpers';

type UsePaidBreakExceptionValidationOptions<TFieldValues extends FieldValues> = {
  isEnabled: boolean;
  setError: UseFormSetError<TFieldValues>;
  clearErrors: UseFormClearErrors<TFieldValues>;
  trigger: UseFormTrigger<TFieldValues>;
  fieldName: FieldPath<TFieldValues>;
  errorMessage?: string;
};

type ValidationResult = {
  isValid: boolean;
  shouldBlockSubmission: boolean;
  invalidDates?: string[];
};

/**
 * Hook that provides validation for paid break exceptions.
 * Validates the store state and sets form errors if validation fails.
 * Should be called before form field validation.
 *
 * @param options - Configuration options for the validation (react hook form methods, feature flag, error message)
 * @returns A function to validate paid break exceptions before form submission
 */
export function usePaidBreakExceptionValidation<TFieldValues extends FieldValues>({
  isEnabled,
  setError,
  clearErrors,
  trigger,
  fieldName,
  errorMessage = 'Accept or reject paid break exceptions before approving the timesheet.',
}: UsePaidBreakExceptionValidationOptions<TFieldValues>) {
  const exceptions = usePaidBreakExceptionStore(useShallow(s => s.exceptions));

  // Clear form error when paid break exceptions become valid
  React.useEffect(() => {
    if (!isEnabled) {
      return;
    }

    const validation = validatePaidBreakExceptions();

    if (validation.isValid) {
      clearErrors(fieldName);
    }
  }, [exceptions, isEnabled, clearErrors, fieldName]);

  /**
   * Validates paid break exceptions and sets errors if needed
   * Should be called before form field validation
   *
   * @param triggerOtherValidations - Whether to trigger other field validations after setting errors (default: true)
   * @returns Validation result indicating if validation passed and if submission should be blocked
   */
  const validate = async (triggerOtherValidations = true): Promise<ValidationResult> => {
    if (!isEnabled) {
      return { isValid: true, shouldBlockSubmission: false };
    }

    const validation = validatePaidBreakExceptions();

    if (!validation.isValid) {
      // Set error state on the store for invalid exceptions
      setExceptionsToError(validation.invalidDates);

      // Set form error to block submission
      setError(fieldName, {
        type: 'manual',
        message: errorMessage,
      });

      // Optionally trigger other field validations to show all errors on form
      if (triggerOtherValidations) {
        await trigger();
      }

      return { isValid: false, shouldBlockSubmission: true, invalidDates: validation.invalidDates };
    }

    // Success, we can clear the errors and be happy about it lol
    clearErrors(fieldName);

    return { isValid: true, shouldBlockSubmission: false };
  };

  return { validate };
}
